/**
 * useOutboundDeliveries — Outbound Delivery Framework
 * -----------------------------------------------------------------------------
 * Hook único de acesso ao envio automático de dados para sistemas externos.
 *
 * Regras de arquitetura:
 *   - 100% via RPC SECURITY DEFINER (nada de SELECT direto em tabelas).
 *   - O mapeamento de destino vem do catálogo do aplicativo e nunca é enviado
 *     pelo navegador (evita gravação em tabelas/colunas indevidas).
 *   - O payload enviado não é exposto na listagem (contém dados do cliente).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-24 v1.1.0 - Reenvio manual ampliado para entregas pendentes,
 *          preservando a invalidação unificada do histórico e da assinatura.
 * RELEASE: 2026-08-24 v1.0.0 - Criação inicial (envio por contrato assinado).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('OutboundDeliveries');

// =============================================================================
// TIPOS
// =============================================================================

/** Configuração do envio automático de uma conexão */
export interface OutboundSubscription {
  id: string;
  event_type: string;
  name: string;
  is_active: boolean;
  filters: Record<string, unknown>;
  created_at: string;
  pending_count: number;
  failed_count: number;
  success_count: number;
}

/** Um envio (tentativa de entrega) registrado no histórico */
export interface OutboundDelivery {
  id: string;
  status: 'pending' | 'processing' | 'success' | 'failed' | 'skipped';
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string | null;
  error: string | null;
  external_ids: Record<string, unknown>;
  finished_at: string | null;
  created_at: string;
  total_count: number;
}

/** Rótulos amigáveis das situações de envio */
export const OUTBOUND_STATUS_LABELS: Record<string, string> = {
  pending: 'Aguardando envio',
  processing: 'Enviando',
  success: 'Enviado',
  failed: 'Falhou',
  skipped: 'Ignorado',
};

/** Mensagem segura para o usuário (nunca propaga detalhe cru do banco) */
function safeMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: string } | null)?.message?.trim() ?? '';
  if (!message || /violates|duplicate key|DETAIL:|\{/i.test(message)) return fallback;
  return message.slice(0, 200);
}

interface UseOutboundDeliveriesOptions {
  connectionId: string | null;
  status?: string | null;
  page?: number;
  pageSize?: number;
  /** Habilita a atualização periódica enquanto existirem envios em andamento */
  enabled?: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export function useOutboundDeliveries({
  connectionId,
  status = null,
  page = 0,
  pageSize = 10,
  enabled = true,
}: UseOutboundDeliveriesOptions) {
  const queryClient = useQueryClient();

  /** Configuração do envio automático da conexão (null = não configurado) */
  const subscriptionQuery = useQuery({
    queryKey: ['outbound-subscription', connectionId],
    enabled: Boolean(connectionId) && enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<OutboundSubscription | null> => {
      const { data, error } = await supabase.rpc('get_outbound_subscription', {
        p_connection_id: connectionId as string,
      });
      if (error) throw error;
      return (data ?? null) as unknown as OutboundSubscription | null;
    },
  });

  /** Histórico paginado de envios (paginação server-side) */
  const deliveriesQuery = useQuery({
    queryKey: ['outbound-deliveries', connectionId, status, page, pageSize],
    enabled: Boolean(connectionId) && enabled,
    staleTime: 10 * 1000,
    // Atualiza sozinho enquanto houver envio em andamento (worker roda a cada minuto)
    refetchInterval: (query) => {
      const rows = (query.state.data ?? []) as OutboundDelivery[];
      const running = rows.some((r) => r.status === 'pending' || r.status === 'processing');
      return running ? 15 * 1000 : false;
    },
    queryFn: async (): Promise<OutboundDelivery[]> => {
      const { data, error } = await supabase.rpc('list_outbound_deliveries', {
        p_connection_id: connectionId as string,
        p_status: status,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as unknown as OutboundDelivery[];
    },
  });

  /** Invalida configuração + histórico da conexão */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['outbound-subscription', connectionId] });
    queryClient.invalidateQueries({ queryKey: ['outbound-deliveries', connectionId] });
  };

  /** Liga/desliga (e cria, se necessário) o envio automático da conexão */
  const saveSubscription = useMutation({
    mutationFn: async (input: { isActive: boolean; filters?: Record<string, unknown> }) => {
      const { data, error } = await supabase.rpc('upsert_outbound_subscription', {
        p_connection_id: connectionId as string,
        p_is_active: input.isActive,
        p_filters: (input.filters ?? {}) as never,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(
        variables.isActive ? 'Envio automático ativado' : 'Envio automático desativado',
      );
    },
    onError: (error: Error) => {
      logger.error('Falha ao salvar envio automático', { message: safeMessage(error, 'erro') });
      toast.error(safeMessage(error, 'Não foi possível salvar o envio automático'));
    },
  });

  /** Recoloca uma entrega elegível na fila para tentativa imediata */
  const retryDelivery = useMutation({
    mutationFn: async (deliveryId: string) => {
      const { error } = await supabase.rpc('retry_outbound_delivery', {
        p_delivery_id: deliveryId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Envio recolocado na fila');
    },
    onError: (error: Error) => {
      logger.error('Falha ao reenviar entrega', { message: safeMessage(error, 'erro') });
      toast.error(safeMessage(error, 'Não foi possível reenviar'));
    },
  });

  const deliveries = deliveriesQuery.data ?? [];

  return {
    subscription: subscriptionQuery.data ?? null,
    isLoadingSubscription: subscriptionQuery.isLoading,
    deliveries,
    totalCount: deliveries[0]?.total_count ?? 0,
    isLoading: deliveriesQuery.isLoading,
    isFetching: deliveriesQuery.isFetching,
    refetch: deliveriesQuery.refetch,
    saveSubscription,
    retryDelivery,
  };
}
